import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, XCircle } from "lucide-react";
import { useAdminApi } from "@/hooks/useAdminApi";
import { errMsg, money } from "@/lib/api";

const BADGE = { expired: "bg-gray-500/15 text-gray-300", awaiting_payment: "bg-sky-500/15 text-sky-300" };
const when = (s) => new Date(s).toLocaleString([], { dateStyle: "short", timeStyle: "short" });

const Row = ({ o, checked, onCheck, onSend, onReject, busy }) => (
  <div data-testid={`reminder-row-${o.id}`} className="flex gap-3 p-4 text-sm">
    <label className="-m-2.5 p-2.5 shrink-0 cursor-pointer self-start"><input type="checkbox" data-testid={`reminder-check-${o.id}`} checked={checked} onChange={onCheck} className="w-5 h-5 accent-emerald-500 block" /></label>
    <div className="flex-1 min-w-0 space-y-1">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono font-semibold">{o.id}</span>
        <span className={`px-2 py-0.5 rounded text-xs capitalize ${BADGE[o.status]}`}>{o.status === "expired" ? "no hash · expired" : "waiting for payment"}</span>
        <span className="font-mono text-emerald-400">{money(o.total)}</span>
      </div>
      <p className="text-gray-300 truncate">{o.email}</p>
      <p className="text-xs text-gray-500 line-clamp-2">{o.items.map((i) => `${i.name}×${i.qty}`).join(", ")}</p>
      <p data-testid={`reminder-count-${o.id}`} className="text-xs text-gray-500">Started {when(o.created_at)} · {o.reminders_sent ? `${o.reminders_sent} reminder${o.reminders_sent > 1 ? "s" : ""} sent, last ${when(o.last_reminded_at)}` : "no reminder sent yet"}</p>
    </div>
    <div className="flex flex-col sm:flex-row gap-2 shrink-0">
      <button data-testid={`reminder-send-${o.id}`} disabled={busy} onClick={onSend} className="h-9 px-3 rounded-md bg-emerald-500 text-[#08090C] font-semibold inline-flex items-center gap-1.5 hover:bg-emerald-400 disabled:opacity-50"><Mail size={14} /> {o.reminders_sent ? "Send again" : "Send"}</button>
      <button data-testid={`reminder-reject-${o.id}`} disabled={busy} onClick={onReject} className="h-9 px-3 rounded-md border border-white/10 inline-flex items-center gap-1.5 hover:border-red-500/60 hover:text-red-400 disabled:opacity-50"><XCircle size={14} /> Reject</button>
    </div>
  </div>
);

export const RemindersTab = () => {
  const call = useAdminApi();
  const [orders, setOrders] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const load = () => call("get", "/admin/reminders").then(setOrders).catch(() => setOrders([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const send = async (ids) => {
    setBusy(true);
    try {
      const r = await call("post", "/admin/reminders/send", { order_ids: ids });
      if (r.sent.length) toast.success(`Reminder sent to ${r.sent.length} customer${r.sent.length > 1 ? "s" : ""}`);
      if (r.failed.length) toast.error(`${r.failed.length} could not be sent`);
      setSel(new Set()); load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const reject = async (o) => {
    if (!window.confirm(`Reject order ${o.id}? The customer won't be able to pay for it anymore.`)) return;
    setBusy(true);
    try { await call("patch", `/admin/orders/${o.id}`, { status: "rejected", admin_note: "No payment received after reminders." }); toast.success("Order rejected"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const all = orders?.length > 0 && sel.size === orders.length;
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-400">Customers who started a checkout in the last 30 days but never sent a transaction ID. The email has a "Complete my order" button and lets them submit their hash if they already paid. Send again if they don't respond, or reject the order.</p>
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-gray-300 h-10 pr-2 cursor-pointer">
          <input type="checkbox" data-testid="reminder-select-all" checked={all} onChange={() => setSel(all ? new Set() : new Set(orders.map((o) => o.id)))} className="w-5 h-5 accent-emerald-500" /> Select all
        </label>
        <button data-testid="reminder-send-selected" disabled={!sel.size || busy} onClick={() => send([...sel])}
          className="h-9 px-4 rounded-md bg-emerald-500 text-[#08090C] font-bold inline-flex items-center gap-2 hover:bg-emerald-400 disabled:opacity-40">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email selected ({sel.size})
        </button>
      </div>
      <div className="rounded-lg border border-white/10 bg-[#0F1117] divide-y divide-white/5">
        {orders === null && <p className="p-6 text-sm text-gray-500">Loading…</p>}
        {orders?.length === 0 && <p data-testid="reminders-empty" className="p-6 text-sm text-gray-500">No unpaid checkouts right now.</p>}
        {orders?.map((o) => <Row key={o.id} o={o} busy={busy} checked={sel.has(o.id)} onCheck={() => toggle(o.id)} onSend={() => send([o.id])} onReject={() => reject(o)} />)}
      </div>
    </div>
  );
};
